import React, { useState } from 'react';
import { PageView, BlogPost } from '../types';
import { ChevronLeft } from 'lucide-react';

const BLOG_POSTS: BlogPost[] = [
  {
    id: 'fiber-cut-isolation',
    title: 'Fiber-Cut Isolation at Scale: How Topological Dependency Trees Eliminate Alert Storms',
    excerpt: 'When a backhoe cuts a primary fiber trunk, standard monitoring tools flood your NOC with 500 CPE-down alarms. Here is how MUSE Telemetry uses dependency correlation to suppress noise.',
    author: 'Senior Infrastructure Architect',
    role: 'MUSE Telemetry Engineering',
    date: 'August 02, 2026',
    readTime: '6 min read',
    category: 'Network Engineering',
    content: [
      'For NOC leaders managing regional fiber networks, the most frustrating operational event is not the failure itself, but the catastrophic noise surge that follows.',
      'When a physical fiber line drops, every single downstream device loses its ping target simultaneously. Standard NMS systems treat each drop as an independent alarm, firing hundreds of tickets into PagerDuty and ticketing queues.',
      'In this technical deep dive, we examine how topological dependency mapping creates hierarchical parent-child relationships across your transport layer. By evaluating time-series correlation alongside topological paths, MUSE Telemetry instantly collapses 200+ cascading CPE drops into a single root-cause fiber cut radius.'
    ]
  },
  {
    id: 'mixed-vendor-roi',
    title: 'Extending Infrastructure ROI: Monitoring Mixed-Vendor Racks Without Licensing Lock-In',
    excerpt: 'Forced hardware refresh cycles driven by software monitoring constraints hurt ISP operating margins. Learn how agentless protocol normalization lets you buy hardware on technical merit.',
    author: 'Principal Telemetry Architect',
    role: 'MUSE Telemetry Engineering',
    date: 'July 28, 2026',
    readTime: '8 min read',
    category: 'ISP Economics',
    content: [
      'Broadband and ISP profit margins depend heavily on capital expenditure efficiency. Yet traditional network management vendors trap operators in single-logo ecosystems.',
      'When a regional ISP expands into a new fiber footprint or acquires legacy DSL infrastructure, they are often forced to choose between a costly rip-and-replace or operating with fragmented visibility.',
      'MUSE Telemetry decouples telemetry collection from hardware manufacturing. By supporting API, SNMP, CLI, and streaming telemetry natively, operators can mix Cisco, Juniper, MikroTik, and legacy DSLAMs without sacrificing single-pane observability.'
    ]
  },
  {
    id: 'undocumented-config-drift',
    title: 'Undocumented Configuration Changes: The Hidden Cause of Midnight BGP Outages',
    excerpt: 'Why do network segments go dark overnight when no hardware failed? Discover how automated CLI config snapshotting and diffing correlates outages with stealth edits.',
    author: 'NOC Operations Team',
    role: 'MUSE Telemetry Engineering',
    date: 'July 15, 2026',
    readTime: '5 min read',
    category: 'Operational Audit',
    content: [
      'Over 60% of critical network outages are not caused by sudden physical hardware failure, but are triggered by human operational error during routine CLI maintenance.',
      'A technician modifies a BGP import filter or lowers a maximum-prefix threshold at 2:00 AM. Hours later, traffic blackholes without warning.',
      'MUSE Telemetry automatically snapshots, versions, and audits running device configurations over time. When an incident occurs, operators can view side-by-side CLI diffs aligned directly on the incident timeline.'
    ]
  }
];

interface BlogPageProps {
  setActivePage: (page: PageView) => void;
  onOpenLeadModal: () => void;
}

export const BlogPage: React.FC<BlogPageProps> = ({ onOpenLeadModal }) => {
  const [selectedPost, setSelectedPost] = useState<BlogPost | null>(null);

  return (
    <div className="blog-minimal">
      <section className="blog-hero-center">
        <div className="section-container">
          <h1 className="b-title">Network Architecture &amp; Telemetry Insights</h1>
          <p className="b-sub">
            Articles, case studies, and technical deep dives written by network engineers, for network engineers.
          </p>
        </div>
      </section>

      <section className="section-padding border-top">
        <div className="section-container">
          {!selectedPost ? (
            <div className="grid-2">
              {BLOG_POSTS.map((post) => (
                <div
                  key={post.id}
                  className="flat-card blog-post-card"
                  onClick={() => { setSelectedPost(post); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                >
                  <div className="card-header-dot">
                    <span className="status-dot green" />
                    <span className="card-tag">{post.category.toUpperCase()} • {post.readTime}</span>
                  </div>
                  <h3>{post.title}</h3>
                  <p>{post.excerpt}</p>
                  <div className="post-footer">
                    <span>{post.author}</span>
                    <span>{post.date}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flat-card post-view-card">
              <button onClick={() => setSelectedPost(null)} className="btn btn-secondary btn-sm mb-3">
                <ChevronLeft size={14} /> Back to Articles
              </button>

              <div className="pill-tag mb-2">
                <span className="status-dot green" />
                <span>{selectedPost.category}</span>
              </div>
              <h1 className="article-title">{selectedPost.title}</h1>
              <div className="article-meta">
                <span>By {selectedPost.author}</span> • <span>{selectedPost.date}</span> • <span>{selectedPost.readTime}</span>
              </div>

              <div className="article-body">
                {selectedPost.content.map((p, idx) => (
                  <p key={idx}>{p}</p>
                ))}
              </div>

              <div className="article-cta border-top mt-4 pt-4">
                <p>Have questions about implementing this architecture on your network?</p>
                <button onClick={onOpenLeadModal} className="btn btn-primary mt-2">
                  Talk to an Infrastructure Architect
                </button>
              </div>
            </div>
          )}
        </div>
      </section>

      <style>{`
        .blog-hero-center {
          padding: 6rem 0 4rem;
          text-align: center;
          max-width: 800px;
          margin: 0 auto;
        }

        .b-title {
          font-size: 2.8rem;
          font-weight: 800;
          color: #ffffff;
          letter-spacing: -0.03em;
          margin-bottom: 1rem;
        }

        .b-sub {
          font-size: 1.1rem;
          color: var(--text-muted);
        }

        .blog-post-card {
          cursor: pointer;
        }

        .post-footer {
          display: flex;
          justify-content: space-between;
          font-family: var(--font-mono);
          font-size: 0.75rem;
          color: var(--text-dim);
          border-top: 1px solid var(--border-color);
          padding-top: 0.75rem;
          margin-top: 1.25rem;
        }

        .post-view-card {
          max-width: 800px;
          margin: 0 auto;
        }

        .article-title {
          font-size: 2.2rem;
          font-weight: 800;
          margin-bottom: 0.75rem;
          color: #ffffff;
        }

        .article-meta {
          font-family: var(--font-mono);
          font-size: 0.78rem;
          color: var(--text-dim);
          margin-bottom: 2rem;
        }

        .article-body p {
          font-size: 1.05rem;
          color: var(--text-muted);
          line-height: 1.7;
          margin-bottom: 1.5rem;
        }

        .article-cta {
          text-align: center;
        }

        .mt-4 { margin-top: 2rem; }
        .pt-4 { padding-top: 2rem; }

        @media (max-width: 800px) {
          .b-title { font-size: 2rem; }
        }
      `}</style>
    </div>
  );
};
